import {makeApiCall, makePublicApiCall} from '../core';
import type {ItemEnvelope, ListEnvelope, ServiceTemplate} from '@ruta/shared';

// Legacy types still used in webapp (to be migrated)
import type {CreateServiceTemplateData, UpdateServiceTemplateData} from '../types';

// =====================
// Service Templates API
// =====================
//
// These wrappers unwrap the standard response envelopes so callers receive
// the resource directly and never read an envelope key themselves.

export const getServiceTemplates = async (
  options: { includeInactive?: boolean } = {},
): Promise<ServiceTemplate[]> => {
  const query = options.includeInactive ? '?includeInactive=true' : '';
  const res = await makeApiCall<ListEnvelope<ServiceTemplate>>('GET', `/service-templates${query}`);
  return res.data;
};

// Public endpoint to get service templates for an organization
export const getPublicServiceTemplates = async (orgId: string): Promise<{ serviceTemplates: ServiceTemplate[]; count: number }> => {
  return makePublicApiCall('GET', `/public/organizations/${orgId}/service-templates`);
};

export const getServiceTemplate = async (
  templateId: string,
): Promise<ServiceTemplate> => {
  const res = await makeApiCall<ItemEnvelope<ServiceTemplate>>('GET', `/service-templates/${templateId}`);
  return res.data;
};

export interface ServiceTemplatePriceResult {
  templateId: string;
  templateName: string;
  pricing: any;
  warnings: unknown[];
}

export const calculateServiceTemplatePrice = async (
  templateId: string,
  pricingData: {
    hours: number;
    squareFootage?: number;
    linearFootage?: number;
    visits?: number;
    serviceOptions?: Record<string, number>;
  },
): Promise<ServiceTemplatePriceResult> => {
  const res = await makeApiCall<ItemEnvelope<ServiceTemplatePriceResult>>(
    'POST',
    `/service-templates/${templateId}/price`,
    pricingData,
  );
  return res.data;
};

export const createServiceTemplate = async (
  templateData: CreateServiceTemplateData,
): Promise<{ template: ServiceTemplate }> => {
  // makeApiCall already uses makeApiCallWithRefresh internally via dynamic import
  // This ensures token has org claims before creating service template
  const res = await makeApiCall<ItemEnvelope<{ template: ServiceTemplate }>>('POST', '/service-templates', templateData);
  return res.data;
};

export const updateServiceTemplate = async (
  templateId: string,
  templateData: UpdateServiceTemplateData,
): Promise<{ template: ServiceTemplate }> => {
  const res = await makeApiCall<ItemEnvelope<{ template: ServiceTemplate }>>(
    'PATCH',
    `/service-templates/${templateId}`,
    templateData,
  );
  return res.data;
};

export const addServiceTemplateGalleryImage = async (
  templateId: string,
  image: { url: string; caption?: string; sourceVisitId?: string; sourcePhotoId?: string },
): Promise<{ template: ServiceTemplate }> => {
  const res = await makeApiCall<ItemEnvelope<{ template: ServiceTemplate }>>(
    'POST',
    `/service-templates/${templateId}/gallery-images`,
    image,
  );
  return res.data;
};

export const deactivateServiceTemplate = async (
  templateId: string,
): Promise<{ id: string; name: string; isActive: boolean; updatedAt: string }> => {
  const res = await makeApiCall<ItemEnvelope<{ id: string; name: string; isActive: boolean; updatedAt: string }>>(
    'DELETE',
    `/service-templates/${templateId}`,
  );
  return res.data;
};

// Keep the old name for backward compatibility but mark as deprecated
/** @deprecated Use deactivateServiceTemplate instead */
export const deleteServiceTemplate = deactivateServiceTemplate;

// =====================
// Service Template Migration API
// =====================

export interface MergeConflictResolution {
  customerId: string;
  fieldSources: {
    rate?: string;
    pricingInputs?: string;
    assignedCrewId?: string;
    scheduleConfig?: string;
    firstVisitDate?: string;
    adminNotes?: string;
    customerNotes?: string;
  };
}

export interface MergeConflict {
  customerId: string;
  customerName: string;
  subscriptions: Array<{
    templateId: string;
    templateName: string;
    rate?: number;
    assignedCrewId?: string;
    crewName?: string;
    subscriptionStatus: string;
  }>;
}

export interface PeriodAssignmentPreview {
  customerId: string;
  customerName: string;
  sourceTemplateName: string;
  assignedPeriodNames: string[];
}

export interface MigrationPreview {
  subscriptionsToMigrate: number;
  conflicts: MergeConflict[];
  periodAssignments?: PeriodAssignmentPreview[];
}

export interface MigrationResult {
  migratedSubscriptions: number;
  deactivatedTemplates: string[];
  completedAt: string;
}

export interface CreateServiceTemplateWithMigrationRequest extends CreateServiceTemplateData {
  migrateFromTemplateIds?: string[];
  migrationConflictResolutions?: MergeConflictResolution[];
  defaultSelectedPeriodIds?: string[];
  seasonMergeStrategy?: 'carry_existing' | 'assign_all';
  dryRun?: boolean;
}

export interface CreateServiceTemplateWithMigrationResponse {
  template: ServiceTemplate;
  migration?: MigrationPreview;
  migrationResult?: MigrationResult;
}

export const createServiceTemplateWithMigration = async (
  templateData: CreateServiceTemplateWithMigrationRequest,
): Promise<CreateServiceTemplateWithMigrationResponse> => {
  const res = await makeApiCall<ItemEnvelope<CreateServiceTemplateWithMigrationResponse>>(
    'POST',
    '/service-templates',
    templateData,
  );
  return res.data;
};

export const previewMigration = async (
  templateData: CreateServiceTemplateWithMigrationRequest,
): Promise<CreateServiceTemplateWithMigrationResponse> => {
  return createServiceTemplateWithMigration({
    ...templateData,
    dryRun: true,
  });
};
